import { zodResolver } from "@hookform/resolvers/zod"
import { IconButton, Typography } from "@mui/material"
import { ChevronLeftOutlined } from "@mui-symbols-material/w300"
import { Link, useNavigate } from "@tanstack/react-router"
import type { FC } from "react"
import { useEffect, useMemo, useRef, useState } from "react"
import { flushSync } from "react-dom"
import { FormProvider, useForm } from "react-hook-form"

import { inventoryDestinationFormSchema } from "../../../../schemas"
import { TRANSFER_TYPE_TO_LABEL, TransferType } from "../../constants"
import { useCreateTransfer } from "../../hooks/useCreateTransfer"
import { useGetTransferComment } from "../../hooks/useGetTransferComment"
import { useGetTransferDocuments } from "../../hooks/useGetTransferDocuments"
import { useUpdateTransfer } from "../../hooks/useUpdateTransfer"
import { type TransferFormValues, transferFormSchema } from "../../schemas/transfer"
import type { TransferFormPallet, TransferFormProductInventory, TransferSaveResult } from "../../types"
import {
  isTransferAdjustmentUpsertFailed,
  isTransferEditable,
  isTransferPartiallyFailed,
  toTransferCreateInput,
  toTransferFormProductInventories,
  toTransferFormValues,
  toTransferUpdateInput,
} from "../../utils/transfer"
import { TransferFormProducts } from "../TransferFormProducts/TransferFormProducts"

import { TransferFormDetails } from "./TransferFormDetails"

import { Button } from "@/components/common/Button"
import CancelDialog from "@/components/common/Modals/CancelDialog"
import type { InventoryByProductItem, SignedUrl, Transfer, WarehouseArea } from "@/graphql/codegen/graphql"
import { SortingOrder, TransferSortField, TransferStatus } from "@/graphql/codegen/graphql"
import { useNavigationConfirmation } from "@/hooks/useNavigationConfirmation"
import { formatWarehouseLabel } from "@/utils/warehouse"

interface TransferFormPageProps {
  type: TransferType
  sourceWarehouseArea: WarehouseArea
  targetWarehouseArea?: WarehouseArea
  transfer?: Transfer
}

const TRANSFER_LIST_ROUTE_TARGET = {
  to: "/app/inventory/transfers",
  search: { field: TransferSortField.CreatedOn, order: SortingOrder.Desc },
} as const

export const TransferFormPage: FC<TransferFormPageProps> = ({
  type,
  sourceWarehouseArea,
  targetWarehouseArea,
  transfer,
}) => {
  const navigate = useNavigate()
  const [inventories, setInventories] = useState<TransferFormProductInventory[]>(() =>
    transfer ? toTransferFormProductInventories(transfer) : []
  )
  const [isInventoryDirty, setIsInventoryDirty] = useState(false)
  const [transferId, setTransferId] = useState<string | undefined>(transfer?.transferId)
  const hasInitializedCommentRef = useRef(false)

  const isInterWarehouse = type === TransferType.InterWarehouse
  const canSaveDraft = !transfer || transfer.status === TransferStatus.Draft
  const isReadOnly = !isTransferEditable(transfer)
  const isCreationFailed =
    !!transfer && isTransferAdjustmentUpsertFailed(transfer) && !isTransferPartiallyFailed(transfer)
  const canCreateTransferOrder = canSaveDraft || isCreationFailed

  const {
    createTransfer,
    documentUploadStatuses: documentUploadStatusesForCreate,
    isUpdatingDocuments: isUpdatingDocumentsForCreate,
  } = useCreateTransfer()
  const {
    updateTransfer,
    documentUploadStatuses: documentUploadStatusesForUpdate,
    isUpdatingDocuments: isUpdatingDocumentsForUpdate,
  } = useUpdateTransfer()
  const { documents: existingDocuments, refetch: refetchDocuments } = useGetTransferDocuments(transferId)
  const {
    comment: existingComment,
    isFetching: isFetchingComment,
    refetch: refetchComment,
  } = useGetTransferComment(transferId)
  const documentUploadStatuses = { ...documentUploadStatusesForCreate, ...documentUploadStatusesForUpdate }
  const isUpdatingDocuments = isUpdatingDocumentsForCreate || isUpdatingDocumentsForUpdate

  const [documentsToDelete, setDocumentsToDelete] = useState<string[]>([])
  const documents = useMemo(
    () => existingDocuments.filter((document) => !documentsToDelete.includes(document.file?.fileId ?? "")),
    [existingDocuments, documentsToDelete]
  )

  const methods = useForm<TransferFormValues>({
    resolver: zodResolver(transferFormSchema),
    mode: "onChange",
    defaultValues: transfer
      ? toTransferFormValues(transfer)
      : {
          type,
          sourceWarehouseAreaId: sourceWarehouseArea.warehouseAreaId,
          targetWarehouseAreaId: targetWarehouseArea?.warehouseAreaId,
          reason: undefined,
          comment: "",
          documents: [],
        },
  })

  // Seeds the comment field once the transfer's existing comment resolves — the Edit page can't
  // include it in defaultValues since it's fetched separately from the Transfer entity itself.
  useEffect(() => {
    if (hasInitializedCommentRef.current || !transferId || isFetchingComment) return

    hasInitializedCommentRef.current = true
    if (existingComment?.value) methods.setValue("comment", existingComment.value)
  }, [transferId, isFetchingComment, existingComment, methods])

  const {
    formState: { isValid, isDirty, isSubmitting, errors },
  } = methods
  const hasDocumentChanges = documentsToDelete.length > 0
  const hasUnsavedChanges = isDirty || isInventoryDirty || hasDocumentChanges
  // isValid doesn't cover manually-set errors from outside the Zod resolver.
  const hasErrors = !!Object.keys(errors).length
  // Only intra-warehouse transfer pallets have a destination.
  const hasValidDestinations =
    isInterWarehouse ||
    inventories.every(({ pallets }) =>
      pallets.every(({ destination }) => inventoryDestinationFormSchema.safeParse(destination).success)
    )
  // Transfer type and warehouse are already valid on init; isValid and hasUnsavedChanges only gate consequential saves for updating a transfer.
  const canSubmitTransferDraft =
    canSaveDraft &&
    !hasErrors &&
    !isSubmitting &&
    hasValidDestinations &&
    (!transferId || (isValid && hasUnsavedChanges))
  const canSubmitTransferOrder =
    isValid &&
    !hasErrors &&
    hasValidDestinations &&
    inventories.length > 0 &&
    inventories.every((inventory) => inventory.pallets.length > 0)

  const { isNavigationBlocked, handleNavigationConfirm, handleNavigationCancel } = useNavigationConfirmation({
    shouldBlock: hasUnsavedChanges,
  })

  const handleProductsAdd = (inventoriesToAdd: InventoryByProductItem[]) => {
    setIsInventoryDirty(true)
    setInventories((prev) => [
      ...prev,
      ...inventoriesToAdd.map((inventoryToAdd) => ({ ...inventoryToAdd, pallets: [] })),
    ])
  }

  const handleProductRemove = (productId: number) => {
    setIsInventoryDirty(true)
    setInventories((prev) => prev.filter((inventory) => inventory.product.productId !== productId))
  }

  const handlePalletsChange = (productId: number, pallets: TransferFormPallet[]) => {
    setIsInventoryDirty(true)
    setInventories((prev) =>
      prev.map((inventory) => (inventory.product.productId === productId ? { ...inventory, pallets } : inventory))
    )
  }

  const handleDocumentDelete = (removedDocument: SignedUrl) => {
    const fileId = removedDocument.file?.fileId

    if (!fileId) return

    setDocumentsToDelete((prev) => [...prev, fileId])
  }

  const upsertTransfer = (formValues: TransferFormValues, isDraft: boolean): Promise<TransferSaveResult> => {
    const documentsToUpload = formValues.documents?.map((document) => document.file)

    return transferId
      ? updateTransfer(toTransferUpdateInput(formValues, inventories, transferId, isDraft), {
          comment: formValues.comment,
          existingCommentId: existingComment?.commentId,
          documentsToDelete,
          documentsToUpload,
        })
      : createTransfer(toTransferCreateInput(formValues, inventories, isDraft), {
          comment: formValues.comment,
          documentsToUpload,
        })
  }

  const handleSubmitTransferDraft = methods.handleSubmit(async (formValues) => {
    const result = await upsertTransfer(formValues, true)

    if (!result.transferId) return

    setTransferId(result.transferId)
    setIsInventoryDirty(false)
    setDocumentsToDelete([])
    refetchDocuments()
    refetchComment()

    methods.reset({ ...formValues, documents: result.hasDocumentUploadFailure ? formValues.documents : [] })
  })

  const handleSubmitTransferOrder = methods.handleSubmit(async (formValues) => {
    const result = await upsertTransfer(formValues, false)

    if (!result.transferId) return

    // Without flushSync, useNavigationConfirmation's blocker still sees the pre-reset dirty
    // state when navigate() fires immediately after.
    flushSync(() => {
      setIsInventoryDirty(false)
      setDocumentsToDelete([])
      methods.reset()
    })

    navigate(TRANSFER_LIST_ROUTE_TARGET)
  })

  return (
    <div className='flex h-full flex-col gap-y-6'>
      <div className='flex items-center justify-between gap-4'>
        <div className='mr-auto flex items-center gap-4'>
          <Link {...TRANSFER_LIST_ROUTE_TARGET}>
            <IconButton aria-label='back to list' className='rounded bg-gray-200 text-primary'>
              <ChevronLeftOutlined />
            </IconButton>
          </Link>
          <Typography variant='h5' color='primary' fontWeight='normal' component='h1'>
            {transfer ? "Edit Transfer" : "New Transfer"}
          </Typography>
        </div>
        <div className='flex gap-x-3'>
          <Link {...TRANSFER_LIST_ROUTE_TARGET}>
            <Button appearance='outlined' data-testid='transfer.form-page.cancel-button'>
              Cancel
            </Button>
          </Link>
          <Button
            appearance='outlined'
            disabled={!canSubmitTransferDraft || isReadOnly}
            onClick={handleSubmitTransferDraft}
            data-testid='transfer.form-page.submit-draft-button'
          >
            Save Draft
          </Button>
          <Button
            appearance='contained'
            disabled={!canSubmitTransferOrder || isReadOnly}
            onClick={handleSubmitTransferOrder}
            data-testid='transfer.form-page.submit-order-button'
          >
            {canCreateTransferOrder ? "Create Transfer Order" : "Update Transfer Order"}
          </Button>
        </div>
      </div>
      <div className='flex gap-x-6 overflow-hidden rounded-lg border border-gray-400 px-6 py-4'>
        <div className='flex-1'>
          <Typography variant='overline' className='text-gray-600'>
            Transfer Type
          </Typography>
          <Typography variant='h5' component='div' className='text-gray-800'>
            {TRANSFER_TYPE_TO_LABEL[type]}
          </Typography>
        </div>
        <div className='flex-1'>
          <Typography variant='overline' className='text-gray-600'>
            Source Warehouse
          </Typography>
          <Typography variant='h5' component='div' className='text-gray-800'>
            {formatWarehouseLabel(sourceWarehouseArea)}
          </Typography>
        </div>
        {isInterWarehouse && targetWarehouseArea && (
          <div className='flex-1'>
            <Typography variant='overline' className='text-gray-600'>
              Target Warehouse
            </Typography>
            <Typography variant='h5' component='div' className='text-gray-800'>
              {formatWarehouseLabel(targetWarehouseArea)}
            </Typography>
          </div>
        )}
      </div>
      <FormProvider {...methods}>
        <div className='grid min-h-0 flex-1 grid-cols-[1fr_380px] gap-6'>
          <div className='min-h-0 overflow-hidden rounded-lg border border-gray-400 p-6'>
            <TransferFormProducts
              sourceWarehouseArea={sourceWarehouseArea}
              targetWarehouseArea={targetWarehouseArea}
              inventories={inventories}
              readOnly={isReadOnly}
              onProductsAdd={handleProductsAdd}
              onProductRemove={handleProductRemove}
              onPalletsChange={handlePalletsChange}
            />
          </div>
          <TransferFormDetails
            documents={documents}
            onDocumentDelete={handleDocumentDelete}
            documentUploadStatuses={documentUploadStatuses}
            isUpdatingDocuments={isUpdatingDocuments}
            readOnly={isReadOnly}
          />
        </div>
      </FormProvider>
      {isNavigationBlocked && <CancelDialog onClose={handleNavigationCancel} onConfirm={handleNavigationConfirm} />}
    </div>
  )
}
